import './IMGEventCentre.less';

import React, { useEffect, useRef } from 'react';
import { eventCentre, MessageTopics } from '@img-arena/front-row-seat';
import { dispatch, useReframe } from 'nike-re-framejs';
import MatchStatisticsScrollable from '../MatchStatisticsScrollable';
import { loggedIn$ } from '../../../turboLive/subs';
import { lang } from '../../../turboLive/utils';
import { isEventCentreBlocked$ } from '../../subs';
import { getAuthTokenAndTimestamp, getImgEventCentreProperties } from '../../utils';
import { checkEventCentreAllowed } from '../../handlers';
import { trackEvent } from '../../../../analytics/gtm';
import i18n from '../../../../common/i18n';

const streamErrorCodes = {
    USER_NOT_LOGGED_IN: 'stream.error.notLoggedIn',
    EVENT_NOT_RUNNING: 'stream.error.notRunning',
    EVENT_OVER: 'stream.error.notRunning',
    EVENT_NOT_STARTED: 'stream.starting.soon',
    STREAM_NOT_AVAILABLE: 'stream.not.available',
    STREAM_FUP: 'stream.fup',
    USER_NOT_VERIFIED: 'stream.error.notVerified',
    FORBIDDEN_STREAM1: 'stream.error.forbiddenStream1',
    FORBIDDEN_STREAM2: 'stream.error.forbiddenStream2',
    FORBIDDEN_STREAM3: 'stream.error.forbiddenStream3',
    GEO_BLOCK: 'stream.geo.blocked',
    TECHNICAL_PROBLEM: 'stream.technical.problem'
};

function IMGEventCentre({ matchId, ufcEventId, tournamentId }) {
    const imgEventCentreRef = useRef();
    const derefSub = useReframe();
    const eventId = ufcEventId;
    const fightId = matchId;
    const loggedIn = derefSub(loggedIn$);
    const isEventCentreBlocked = derefSub(isEventCentreBlocked$);
    const eventCentreObject = useRef(null);
    const [isVideoPlaybackAllowed, operatorId] = getImgEventCentreProperties();

    //musi to byt tu kvoli i18n
    function getMessage(error) {
        const message =
            error && error.get('code')
                ? i18n(streamErrorCodes[error.get('code')])
                : i18n(streamErrorCodes.TECHNICAL_PROBLEM);
        const params = error && error.get('params');
        if (!params || params.length === 0) {
            return message;
        }

        return params.reduce(
            (mess, param, i) =>
                mess.replace(new RegExp('\\{' + i + '\\}', 'g'), !isNaN(param) ? formatNumber(param) : param),
            message
        );
    }

    useEffect(() => {
        if (!isEventCentreBlocked && loggedIn && tournamentId) {
            dispatch(checkEventCentreAllowed(tournamentId));
        }
    }, [isEventCentreBlocked, loggedIn, tournamentId]);

    useEffect(() => {
        if (!isEventCentreBlocked && loggedIn && imgEventCentreRef.current) {
            if (!eventCentreObject.current) {
                eventCentreObject.current = eventCentre({
                    operator: operatorId,
                    sport: 'ufc',
                    targetModule: 'fight',
                    eventId,
                    version: '5.1.x',
                    targetElementSelector: '#img-arena-event-centre',
                    language: lang,
                    options: {
                        videoPlaybackEnabled: isVideoPlaybackAllowed
                    },
                    initialContext: {
                        view: 'Fight',
                        fightId
                    }
                });
                if (isVideoPlaybackAllowed) {
                    eventCentreObject.current.on(MessageTopics.VIDEO_PLAYBACK_AUTH_REQUEST, () =>
                        getAuthTokenAndTimestamp(response =>
                            eventCentreObject.current.emit(MessageTopics.VIDEO_PLAYBACK_AUTH_RESPONSE, {
                                authToken: response.token,
                                auth: response.token,
                                timestamp: response.timestamp,
                                operatorId: response.providerId
                            })
                        )
                    );
                }
            }
            trackEvent('IMG Event Centre', 'loaded');

            eventCentreObject.current.emit(MessageTopics.CONTEXT_UPDATE, {
                view: 'Fight',
                fightId
            });
        }
    }, [isEventCentreBlocked, loggedIn, imgEventCentreRef, eventId, fightId, isVideoPlaybackAllowed, operatorId]);

    if (!loggedIn || isEventCentreBlocked) {
        return (
            <MatchStatisticsScrollable>
                <div className="ufc-arena-login-required c-default-light br-radius-xs my-30 mx-15 py-10 px-15 text-center">
                    {!loggedIn ? i18n('ufc.eventCentre.loginRequired') : getMessage(isEventCentreBlocked)}
                </div>
            </MatchStatisticsScrollable>
        );
    }

    return (
        <div
            id="img-arena-event-centre"
            className="img-arena-event-centre full-height p-rel overflow-hidden"
            ref={imgEventCentreRef}
        />
    );
}

function formatNumber(input) {
    return parseFloat(input)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export default IMGEventCentre;
